import { HierarcicalObjectReference, ObjectId } from "@novorender/data-js-api";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

import { Tree } from "components/objectTree/types";
import { getParentPaths } from "components/objectTree/utils";
import { ModelTreeNodeData } from "features/modelTree/types";
import { AppObjectDB } from "utils/appObjectDb";
import { fetchDescendantsBatch } from "utils/preloadDescendants";
import { SelectionSet } from "utils/selectionSet";

/**
 * Maximum number of nodes to fetch descendants for in a single effect run.
 * Acts as a safety net to prevent unbounded network load even when many
 * ancestor nodes are present.
 */
const MAX_FETCH_PER_RUN = 50;

/**
 * Proactively fetches `descendants` for non-leaf tree nodes that don't have
 * them yet.  This allows {@link getGroupStateForIdSet} to propagate selection
 * state (e.g. from lasso / area-select) up to folder nodes even before the
 * user expands them in the UI.
 *
 * To avoid overwhelming the backend with requests, the fetch list is **scoped
 * to ancestor paths** of the current selection: only folder nodes that are
 * direct ancestors of a selected object already present in the tree are
 * considered.  A per-run budget ({@link MAX_FETCH_PER_RUN}) provides an
 * additional safety net.  Requests are batched with limited concurrency and
 * nodes whose own id overlaps the current selection are fetched first so the UI
 * can show accurate state as early as possible.
 *
 * Once all outstanding nodes are populated the hook "touches" the tree
 * (`setTree(t => ({...t}))`) so that memos depending on the tree reference
 * recompute with the newly-available descendant data.
 */
export function usePreloadDescendants(
    tree: Tree<ModelTreeNodeData>,
    db: AppObjectDB | undefined,
    idSets: ReadonlyArray<SelectionSet>,
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>,
): void {
    const setTreeRef = useRef(setTree);
    setTreeRef.current = setTree;

    useEffect(() => {
        const isSelected = (id: ObjectId) => idSets.some((ids) => ids.has(id));

        if (!db || idSets.length === 0) {
            return;
        }

        // Build the set of ancestor paths for selected objects that are
        // already present in the tree.  We only need descendants for folder
        // nodes on these ancestor paths – all other nodes are unrelated to
        // the current selection and can be skipped.
        const ancestorPaths = new Set<string>();
        for (const node of Object.values(tree)) {
            if (isSelected(node.data.object.id)) {
                for (const p of getParentPaths(node.path)) {
                    ancestorPaths.add(p);
                }
            }
        }

        // Collect non-leaf nodes that are in the tree but haven't had their
        // descendants fetched yet, if they either lie on an ancestor path of a
        // selected object or are themselves selected.  A selected folder needs
        // its own descendants to render "all": when a parent folder is selected
        // every sibling folder's own id is added to the selection too, but the
        // siblings are collapsed so they sit on no in-tree node's ancestor path
        // — without this they stay stuck on the indeterminate "some" state until
        // a manual collapse + re-expand.  This includes partially-loaded nodes
        // (children present but not all pages loaded) so that selection state
        // can propagate through unloaded portions.  This avoids fetching
        // descendants for the (potentially very large) set of unrelated folder
        // nodes. Exclude the root path ("") so we never preload descendants for
        // the entire scene.
        const nodesToFetch: HierarcicalObjectReference[] = [];
        for (const node of Object.values(tree)) {
            if (
                node.path !== "" &&
                !node.isLeaf &&
                !node.loading &&
                !node.data.object.descendants &&
                (ancestorPaths.has(node.path) || isSelected(node.data.object.id))
            ) {
                nodesToFetch.push(node.data.object);
            }
        }

        if (nodesToFetch.length === 0) {
            return;
        }

        // Prioritise nodes whose own id is in the selection – these are the
        // ones most likely to need descendant data for the current UI state.
        nodesToFetch.sort((a, b) => {
            const aSelected = isSelected(a.id) ? 0 : 1;
            const bSelected = isSelected(b.id) ? 0 : 1;
            return aSelected - bSelected;
        });

        // Apply per-run budget cap to prevent unbounded network load.
        if (nodesToFetch.length > MAX_FETCH_PER_RUN) {
            nodesToFetch.length = MAX_FETCH_PER_RUN;
        }

        const abortController = new AbortController();

        fetchDescendantsBatch(db, nodesToFetch, abortController.signal).then((populated) => {
            if (populated && !abortController.signal.aborted) {
                // Create a new tree object reference so that WeakMap-keyed caches
                // in treeHelpers.ts are invalidated and the memoised counters are
                // rebuilt with the freshly populated descendants.
                setTreeRef.current((t) => ({ ...t }));
            }
        });

        return () => abortController.abort();
        // Re-run when the tree identity changes (nodes added/removed) or when
        // the selection changes.  The reducer hands a fresh `ids` record on every
        // selection mutation, so `idSets` reliably triggers re-runs; nodes whose
        // descendants were already populated are skipped by the filter above, so
        // redundant network requests are avoided.
    }, [tree, db, idSets]);
}
